/** /check/<CODE>: a resident going out of town asks the sheriff's office to check on the house (no sign-in). */
import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../shared/api.ts';
import { BlueMark } from '../shared/BlueMark.tsx';
import { ErrorBox, Notice, Spinner, useAction, useLoad } from '../shared/ui.tsx';

const EMPTY = { residentName: '', phone: '', email: '', address: '', city: '', zip: '', startsOn: '', endsOn: '', contactName: '', contactPhone: '', details: '', emailUpdates: true, website: '' };

export function HouseCheckRequest({ code }: { code: string }) {
  const r = useLoad(() => api.get<any>(`/api/public/house-checks/${encodeURIComponent(code)}`), [code]);
  const a = useAction();
  const [f, setF] = useState(EMPTY);
  const [done, setDone] = useState<{ number: string } | null>(null);
  useEffect(() => {
    if (r.data?.agencyName) document.title = `House check request · ${r.data.agencyName}`;
  }, [r.data]);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    a.run(async () => {
      setDone(await api.post(`/api/public/house-checks/${encodeURIComponent(code)}`, f));
      window.scrollTo(0, 0);
    });
  };
  return (
    <div className="xfer-page tip-page">
      <header className="xfer-top">
        <BlueMark size={34} />
        <div>
          <strong>{r.data?.agencyName ?? 'Sheriff’s Office'}</strong>
          <div className="small">Vacation house checks</div>
        </div>
      </header>
      <main className="xfer-main stack">
        {r.error ? (
          <ErrorBox error={r.error} />
        ) : !r.data ? (
          <Spinner />
        ) : done ? (
          <div className="card tip-done stack">
            <h1>Got it. Your request was sent.</h1>
            <p>Deputies will check on the house while you’re away, as calls allow. If your plans change, call us and give this number:</p>
            <dl className="tip-codes">
              <div>
                <dt>Request number</dt>
                <dd>{done.number}</dd>
              </div>
            </dl>
            <button className="btn" onClick={() => (setDone(null), setF(EMPTY))}>
              Send another request
            </button>
          </div>
        ) : (
          <form className="card stack tip-form" onSubmit={submit}>
            <h1>Ask for house checks while you’re away</h1>
            <Notice tone="bad">
              <strong>Emergency? Call 911.</strong> This form isn’t watched around the clock.
            </Notice>
            <p className="small muted">Deputies drive by and check doors and windows when they can. Up to {r.data.maxDays} days.</p>
            <ErrorBox error={a.error} />
            <div className="grid2">
              <label className="field">
                <span className="field-l">Your name *</span>
                <input required maxLength={120} value={f.residentName} onChange={set('residentName')} autoComplete="name" />
              </label>
              <label className="field">
                <span className="field-l">Phone</span>
                <input maxLength={40} inputMode="tel" value={f.phone} onChange={set('phone')} autoComplete="tel" />
              </label>
            </div>
            <label className="field">
              <span className="field-l">Email</span>
              <input type="email" maxLength={200} value={f.email} onChange={set('email')} autoComplete="email" />
            </label>
            {f.email && (
              <label className="check">
                <input type="checkbox" checked={f.emailUpdates} onChange={(e) => setF({ ...f, emailUpdates: e.target.checked })} /> Email me each time a deputy checks the house
              </label>
            )}
            <label className="field">
              <span className="field-l">Address to check *</span>
              <input required maxLength={200} value={f.address} onChange={set('address')} autoComplete="street-address" />
            </label>
            <div className="grid2">
              <label className="field">
                <span className="field-l">City</span>
                <input maxLength={80} value={f.city} onChange={set('city')} />
              </label>
              <label className="field">
                <span className="field-l">ZIP</span>
                <input maxLength={10} inputMode="numeric" value={f.zip} onChange={set('zip')} />
              </label>
              <label className="field">
                <span className="field-l">Leaving *</span>
                <input type="date" required value={f.startsOn} onChange={set('startsOn')} />
              </label>
              <label className="field">
                <span className="field-l">Back *</span>
                <input type="date" required value={f.endsOn} min={f.startsOn || undefined} onChange={set('endsOn')} />
              </label>
            </div>
            <label className="field">
              <span className="field-l">Anything deputies should know</span>
              <textarea rows={4} maxLength={3000} value={f.details} onChange={set('details')} placeholder="Cars that will be there, who may stop by (house sitter, pet feeder), alarm, lights on timers, dogs, gate code" />
            </label>
            <div className="grid2">
              <label className="field">
                <span className="field-l">Someone local we can call</span>
                <input maxLength={120} value={f.contactName} onChange={set('contactName')} placeholder="Name" />
              </label>
              <label className="field">
                <span className="field-l">Their phone</span>
                <input maxLength={40} inputMode="tel" value={f.contactPhone} onChange={set('contactPhone')} />
              </label>
            </div>
            <label className="tip-hp" aria-hidden="true">
              Website
              <input tabIndex={-1} autoComplete="off" value={f.website} onChange={set('website')} />
            </label>
            <div>
              <button className="btn primary" disabled={a.busy}>
                {a.busy ? 'Sending…' : 'Send request'}
              </button>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}
